import {
  Component,
  HostBinding,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
  signal,
  computed,
  input,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import { ReplaySubject, distinctUntilChanged, takeUntil } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { EmbeddedMedia, ScreenShareStatus } from 'messages/websocket.messages';

import { BrowserService } from '../../services/browser.service';
import Keyboard from '../../social-pixi-canvas/keyboard';
import { ExtendedPresenter, socialAppSelectors } from '../../state/app';
import { PresentationDrawerComponent } from '../presentation-drawer/presentation-drawer.component';

@Component({
  selector: 'app-presentation-presenter',
  templateUrl: './presentation-presenter.component.html',
  styleUrls: ['./presentation-presenter.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PresentationPresenterComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private browserService = inject(BrowserService);

  @HostBinding('class.group-share-active')
  public readonly isGroupSharing = input<boolean>(false);

  protected readonly captionsOpened = input<boolean>(false);

  private destroyed$ = new ReplaySubject<boolean>(1);
  protected drawer: PresentationDrawerComponent | undefined;
  private readonly screenShareStatus = signal<ScreenShareStatus>(ScreenShareStatus.NONE);
  private keyboard = Keyboard.getInstance();
  protected readonly isPresentersDrawerOpen = signal<boolean>(true);
  protected readonly isPresenting = signal<boolean>(false);
  protected readonly isMobile = computed(() => this.browserService.isMobile());
  protected readonly speakingParticipantsV1 = signal<boolean>(false);
  protected readonly activePresenters = signal<number>(0);
  protected readonly vimeoEmbed = signal<boolean>(false);
  protected readonly embeddedMedia = signal<EmbeddedMedia | undefined>(undefined);
  private readonly mediaUser = signal<ExtendedPresenter | undefined>(undefined);
  protected readonly presentationV3 = signal<boolean>(false);
  protected readonly screenShareActive = computed(() => this.screenShareStatus() === ScreenShareStatus.ACTIVE);
  protected readonly drawerOpenState = computed(() => this.drawer?.isDrawerOpen() ?? false);

  public constructor() {
    this.store
      .select(socialAppSelectors.getScreenShareState)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ status }) => this.screenShareStatus.set(status));

    this.store
      .select(socialAppSelectors.getPresenterDetails)
      .pipe(distinctUntilChanged(isEqual), takeUntil(this.destroyed$))
      .subscribe((presenters: ExtendedPresenter[]) => {
        this.activePresenters.set(
          presenters ? presenters.filter((slot: ExtendedPresenter) => !slot.videoMuted).length : 0,
        );
        const mediaUser = presenters.find((slot: ExtendedPresenter) => slot.media);
        // don't mutate state if the mediaUser is the same and just muting
        if (
          this.mediaUser() &&
          mediaUser &&
          mediaUser.userId === this.mediaUser()?.userId &&
          (this.mediaUser()?.audioMuted !== mediaUser.audioMuted ||
            this.mediaUser()?.videoMuted !== mediaUser.videoMuted)
        ) {
          this.mediaUser.set(mediaUser);
          return;
        }
        this.mediaUser.set(mediaUser);
        this.embeddedMedia.set(mediaUser?.media);
      });

    this.store
      .select(socialAppSelectors.isUserPresenting)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresenting) => this.isPresenting.set(isPresenting));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.speaking_participants_v1)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((speakingParticipantsV1) => this.speakingParticipantsV1.set(speakingParticipantsV1));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.vimeo_embed)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((vimeo_embed) => {
        this.vimeoEmbed.set(vimeo_embed);
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.presentation_v3)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((presentation_v3) => {
        this.presentationV3.set(presentation_v3);
      });
  }
  public ngOnInit(): void {
    this.drawer = PresentationDrawerComponent.getInstance();
    this.keyboard.disabled = true;
  }

  public ngOnDestroy(): void {
    this.keyboard.disabled = false;
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
  @HostBinding('class.drawer-open')
  public get isDrawerOpen(): boolean {
    return this.drawerOpenState();
  }

  @HostBinding('class.screenshare')
  public get screenShareActiveClass(): boolean {
    return this.screenShareActive();
  }

  protected setIsPresentersDrawerOpen(isOpen: boolean): void {
    this.isPresentersDrawerOpen.set(isOpen);
  }
}
